<template>
  <div class="request-password-reset">
    <div class="request-password-reset__card">
      <div class="request-password-reset__header">
        <img
          src="../../assets/logo.png"
          alt="Logo"
          class="request-password-reset__logo"
        />
        <h1 class="request-password-reset__title">Reset hasła</h1>
      </div>

      <form
        v-if="!isSuccess"
        class="request-password-reset__form"
        @submit="onSubmit"
      >
        <div class="request-password-reset__form-group">
          <label
            for="email"
            class="request-password-reset__label"
          >
            Adres email
          </label>
          <input
            id="email"
            v-model="email"
            type="email"
            class="request-password-reset__input"
            :class="{ 'request-password-reset__input--error': emailError }"
            placeholder="Wprowadź swój adres email"
          />
          <div
            v-if="emailError"
            class="request-password-reset__message"
          >
            <small class="request-password-reset__error">{{ emailError }}</small>
          </div>
        </div>

        <button
          type="submit"
          class="request-password-reset__button"
          :disabled="isLoading"
        >
          <span
            v-if="isLoading"
            class="request-password-reset__spinner"
          ></span>
          <span
            v-else
            class="request-password-reset__button__text"
            >Wyślij link do resetu hasła</span
          >
        </button>

        <div
          v-if="error"
          class="request-password-reset__message request-password-reset__message--center"
        >
          <small class="request-password-reset__error">{{ error }}</small>
        </div>

        <router-link
          to="/login"
          class="request-password-reset__button request-password-reset__button--return"
        >
          Powrót do logowania
        </router-link>
      </form>

      <div
        v-else
        class="request-password-reset__success"
      >
        <div class="request-password-reset__icon request-password-reset__icon--success">
          <svg
            class="request-password-reset__svg"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 class="request-password-reset__subtitle">Link wysłany</h2>
        <p class="request-password-reset__message">
          Jeśli podany adres email istnieje w naszej bazie, wysłaliśmy na niego link do resetu hasła. Sprawdź swoją
          skrzynkę i postępuj zgodnie z instrukcjami w wiadomości.
        </p>
        <router-link
          to="/login"
          class="request-password-reset__button"
        >
          Powrót do logowania
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { UserService } from '@/api/user/userService';
import { useField, useForm } from 'vee-validate';
import { z } from 'zod';
import { validateSchema } from '@/pages/Schemas/Schemas';

const emailSchema = z.object({
  email: z.string().email('Niepoprawny format adresu email').min(1, 'Email jest wymagany'),
});

const buttonState = ref<'pending' | 'success' | 'error'>('pending');
const error = ref<string | null>(null);
const isLoading = ref(false);
const isSuccess = ref(false);

const { handleSubmit: handleFormSubmit } = useForm({
  validationSchema: validateSchema(emailSchema),
  initialValues: {
    email: '',
  },
});

const { value: email, errorMessage: emailError } = useField('email');

const onSubmit = handleFormSubmit(async (values) => {
  try {
    error.value = null;
    isLoading.value = true;
    buttonState.value = 'pending';

    await UserService.requestPasswordReset(values.email);

    buttonState.value = 'success';
    isSuccess.value = true;
  } catch (err) {
    buttonState.value = 'error';
    error.value = 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie później.';
    console.error(err);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style lang="scss" scoped>
@use 'sass:color';

.request-password-reset {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $white-100 0%, $brown-200 100%);
  padding: 20px;

  &__card {
    display: flex;
    flex-direction: column;
    gap: 4rem;
    width: 100%;
    max-width: 400px;
    padding: 4rem 2.5rem;
    background: $white;
    border-radius: 1rem;
  }

  &__header {
    text-align: center;
  }

  &__logo {
    width: 180px;
    height: auto;
    margin-bottom: 1.5rem;
  }

  &__title {
    font-size: clamp(3.6rem, 4vw, 4.8rem);
    font-weight: 700;
    color: $black-400;
    margin: 0;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  &__form-group {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    position: relative;
  }

  &__label {
    font-size: 2rem;
    font-weight: 700;
    color: $black-400;
    transition: color 0.2s ease;
  }

  &__input {
    height: 3.5rem;
    padding: 0 1.25rem;
    border: 2px solid $brown-400;
    border-radius: 0.75rem;
    font-size: 1.75rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background-color: $white;
    color: $black-400;
    width: 100%;

    &::placeholder {
      color: $brown-700;
      font-size: 1.5rem;
      opacity: 0.7;
    }

    &:focus {
      outline: none;
      border-color: $brown-200;
      box-shadow: 0 0 0 4px rgba($brown-200, 0.15);
    }

    &--error {
      border-color: #f33;

      &:focus {
        box-shadow: 0 0 0 4px rgb(255 51 51 / 15%);
      }
    }
  }

  &__message {
    min-height: 0;
    font-size: 1.5rem;
    color: $black-400;
    line-height: 1.6;
    max-width: 32rem;
    margin: 0 auto;

    &--center {
      text-align: center;
    }

    &:has(.request-password-reset__error:not(:empty)) {
      display: block;
      min-height: 1.25rem;
    }
  }

  &__error {
    font-size: 1.5rem;
    color: #f33;
    display: block;
    font-weight: 500;
  }

  &__button {
    height: 4.5rem;
    background: $brown-200;
    color: $white;
    border: none;
    border-radius: 0.75rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    font-size: 1.7rem;
    font-weight: 600;
    width: 100%;

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: color.adjust($brown-200, $lightness: -5%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($brown-200, 0.3);
    }

    &:active:not(:disabled) {
      background: color.adjust($brown-200, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba($brown-200, 0.2);
    }

    &--return {
      margin-top: 1rem;
      background: $white;
      color: $brown-200;
      border: 2px solid $brown-200;

      &:hover:not(:disabled) {
        background: rgba($brown-200, 0.1);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba($brown-200, 0.2);
      }

      &:active:not(:disabled) {
        background: rgba($brown-200, 0.15);
        transform: translateY(0);
        box-shadow: 0 2px 6px rgba($brown-200, 0.1);
      }
    }

    &__text {
      font-size: 1.7rem;
      font-weight: 600;
    }
  }

  &__spinner {
    width: 2rem;
    height: 2rem;
    border: 2px solid rgb(255 255 255 / 30%);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s linear infinite;
  }

  &__link {
    font-size: 1.75rem;
    color: $brown-200;
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0.5rem;
    text-align: center;

    &:hover {
      opacity: 0.8;
    }
  }

  &__success {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    padding: 2rem 0;
  }

  &__icon {
    margin-bottom: 0;
    background: rgba($status-green, 0.1);
    padding: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;

    &--success &__svg {
      color: $status-green;
    }

    &:hover {
      transform: scale(1.05);
    }
  }

  &__svg {
    width: 4.8rem;
    height: 4.8rem;
    filter: drop-shadow(0 2px 4px rgba($status-green, 0.2));
  }

  &__subtitle {
    font-size: 2.4rem;
    font-weight: 700;
    color: $black-400;
    margin: 0;
    line-height: 1.2;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
